/**
 * Grafana Faro Monitoring Composable
 * Provides a clean API for tracking custom events and errors in the Admin UI
 */

declare global {
    interface Window {
        GrafanaFaroWebSdk?: {
            faro?: {
                api: {
                    pushEvent: (name: string, attributes?: Record<string, any>, type?: string) => void;
                    pushError: (error: Error, context?: Record<string, any>) => void;
                    pushMeasurement: (measurement: { type: string; values: Record<string, any> }) => void;
                    setUser: (user: { id: string; email?: string; username?: string }) => void;
                };
            };
        };
    }
}

export function useMonitoring() {
    /**
     * Check if Faro SDK is available
     */
    const isAvailable = (): boolean => {
        return !!(window.GrafanaFaroWebSdk?.faro);
    };

    /**
     * Track a custom event
     * @param name - Event name (e.g., "mail_processed")
     * @param attributes - Additional event attributes/context
     */
    const trackEvent = (name: string, attributes?: Record<string, any>): void => {
        if (isAvailable()) {
            window.GrafanaFaroWebSdk!.faro!.api.pushEvent(name, attributes, 'custom');
        } else {
            console.debug(`[Monitoring] Would track event: ${name}`, attributes);
        }
    };

    /**
     * Track an error
     * @param error - Error object or message
     * @param context - Additional context about the error
     */
    const trackError = (error: Error | string, context?: Record<string, any>): void => {
        const errorObj = typeof error === 'string' ? new Error(error) : error;

        if (isAvailable()) {
            window.GrafanaFaroWebSdk!.faro!.api.pushError(errorObj, context);
        } else {
            console.error('[Monitoring] Error:', errorObj, context);
        }
    };

    /**
     * Set the current user context
     * @param userId - User identifier
     * @param email - User email (optional)
     * @param username - User username (optional)
     */
    const setUser = (userId: string, email?: string, username?: string): void => {
        if (isAvailable()) {
            window.GrafanaFaroWebSdk!.faro!.api.setUser({
                id: userId,
                email,
                username,
            });
        } else {
            console.debug('[Monitoring] Would set user:', {userId, email, username});
        }
    };

    /**
     * Track a page view (manual, if needed beyond automatic tracking)
     * @param pageName - Name of the page
     * @param metadata - Additional page metadata
     */
    const trackPageView = (pageName: string, metadata?: Record<string, any>): void => {
        if (isAvailable()) {
            window.GrafanaFaroWebSdk!.faro!.api.pushMeasurement({
                type: 'page_view',
                values: {
                    name: pageName,
                    ...metadata,
                },
            });
        } else {
            console.debug('[Monitoring] Would track page view:', pageName, metadata);
        }
    };

    // Admin-specific event tracking helpers

    /**
     * Track admin login
     */
    const trackAdminLogin = (data: {
        userId: string;
        username?: string;
    }): void => {
        trackEvent('admin_login', data);
    };

    /**
     * Track admin logout
     */
    const trackAdminLogout = (): void => {
        trackEvent('admin_logout');
    };

    /**
     * Track customer search
     */
    const trackCustomerSearch = (data: {
        searchTerm?: string;
        resultCount?: number;
    }): void => {
        trackEvent('customer_search', data);
    };

    /**
     * Track customer view
     */
    const trackCustomerViewed = (data: {
        customerId: string;
    }): void => {
        trackEvent('customer_viewed', data);
    };

    /**
     * Track mailbox creation
     */
    const trackMailboxCreated = (data: {
        mailboxId: string;
        customerId: string;
        mailCenterId: string;
        planId?: string;
    }): void => {
        trackEvent('mailbox_created', data);
    };

    /**
     * Track mailbox activation
     */
    const trackMailboxActivated = (data: {
        mailboxId: string;
        customerId: string;
    }): void => {
        trackEvent('mailbox_activated', data);
    };

    /**
     * Track mail task
     */
    const trackMailTask = (data: {
        mailId: string;
        mailboxId: string;
        type: 'forward' | 'forward-collect-costs' | 'scan' | 'shred' | 'dispose' | 'unknown';
    }): void => {
        trackEvent('mail_task', data);
    };

    /**
     * Track mail processing
     */
    const trackMailProcess = (data: {
        count?: number;
    }): void => {
        trackEvent('mail_process', data);
    };

    /**
     * Track mail received (incoming mail logged)
     */
    const trackMailReceived = (data: {
        mailboxId: string;
        mailType: 'mail' | 'package';
        count?: number;
    }): void => {
        trackEvent('mail_received', data);
    };

    /**
     * Track refund issued
     */
    const trackRefundIssued = (data: {
        customerId: string;
        amount: number;
        reason?: string;
        transactionId?: string;
    }): void => {
        trackEvent('refund_issued', data);
    };

    /**
     * Track manual payment
     */
    const trackManualPayment = (data: {
        customerId: string;
        amount: number;
        method?: string;
    }): void => {
        trackEvent('manual_payment', data);
    };

    /**
     * Track USPS form approval
     */
    const trackUSPSFormApproved = (data: {
        customerId: string;
        mailboxId?: string;
        formType: string;
    }): void => {
        trackEvent('usps_form_approved', data);
    };

    /**
     * Track USPS form rejection
     */
    const trackUSPSFormRejected = (data: {
        customerId: string;
        mailboxId?: string;
        formType: string;
        reason?: string;
    }): void => {
        trackEvent('usps_form_rejected', data);
    };

    /**
     * Track batch operation
     */
    const trackBatchOperation = (data: {
        operation: string;
        itemCount: number;
        success?: boolean;
    }): void => {
        trackEvent('batch_operation', data);
    };

    /**
     * Track report generation
     */
    const trackReportGenerated = (data: {
        reportType: string;
        startDate?: string;
        endDate?: string;
    }): void => {
        trackEvent('report_generated', data);
    };

    /**
     * Track settings change
     */
    const trackSettingsChanged = (data: {
        settingCategory: string;
        settingName?: string;
    }): void => {
        trackEvent('settings_changed', data);
    };

    return {
        // Core tracking functions
        trackEvent,
        trackError,
        setUser,
        trackPageView,
        isAvailable,

        // Admin-specific helpers
        trackAdminLogin,
        trackAdminLogout,
        trackCustomerSearch,
        trackCustomerViewed,
        trackMailboxCreated,
        trackMailboxActivated,
        trackMailProcess,
        trackMailTask,
        trackMailReceived,
        trackRefundIssued,
        trackManualPayment,
        trackUSPSFormApproved,
        trackUSPSFormRejected,
        trackBatchOperation,
        trackReportGenerated,
        trackSettingsChanged,
    };
}